import { ApolloClient, useApolloClient } from '@apollo/client';
import { Spinner, Stack, Text } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil } from '@sixfold/typed-primitives';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ErrorPlaceholder } from '../../components/error_placeholder';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { CompanyGroupExplainer } from '../components/company_group_explainer';
import { CompanyGroupForm } from '../components/company_group_form';
import { CompanyGroupCompany, CompanyGroupLink } from '../components/company_group_graph';
import { CompanyGroupHub, CompanyGroupMembers } from '../components/company_group_members';
import { CompanyGroupNetwork } from '../components/company_group_network';
import { CompanyGroupRelationship, CompanyGroupRole, fetchCompanyGroupRelationships } from '../fetch_relationships';

interface CompanyGroup {
  companyId: string;
  companies: CompanyGroupCompany[];
  links: CompanyGroupLink[];
}

type GroupResult = CompanyGroup | { companyId: string; error: Error };

interface CompanyGroupContainerProps {
  companyName: string | null;
}

export const CompanyGroupContainer: React.FC<CompanyGroupContainerProps> = ({ companyName }) => {
  const apolloClient = useApolloClient();
  const { company_id } = useParams() as { company_id: string };
  const [result, setResult] = useState<GroupResult | undefined>(undefined);
  const [reloadCount, setReloadCount] = useState(0);
  const isAdmin = useIsAdmin();
  const hasCompanyRelationshipPermission = useHasScopes([BackofficeScope.companyWriteNetworkRelations]);
  const isPrivilegedUser = isAdmin || hasCompanyRelationshipPermission;

  useEffect(() => {
    let cancelled = false;
    void loadCompanyGroup(apolloClient, company_id)
      .catch((error: Error) => ({ companyId: company_id, error }))
      .then((loaded) => {
        if (!cancelled) {
          setResult(loaded);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apolloClient, company_id, reloadCount]);

  const currentResult = result?.companyId === company_id ? result : undefined;

  if (isNil(currentResult)) {
    return <Spinner />;
  }

  if ('error' in currentResult) {
    return <ErrorPlaceholder error={currentResult.error} />;
  }

  const currentCompany = currentResult.companies.find((company) => company.isCurrent);
  const role = getRole(currentCompany);
  const form =
    isPrivilegedUser && role !== 'member' ? (
      <CompanyGroupForm
        companyId={company_id}
        companyName={companyName ?? `#${company_id}`}
        role={role}
        onCreated={() => setReloadCount((count) => count + 1)}
      />
    ) : null;

  if (currentResult.links.length === 0) {
    return (
      <Stack direction="column" gap="medium">
        <CompanyGroupExplainer />
        <Text>This company is not part of a company group.</Text>
        {form}
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="medium">
      <CompanyGroupHub companies={currentResult.companies} />
      <CompanyGroupExplainer />
      {form}
      <CompanyGroupMembers companies={currentResult.companies} links={currentResult.links} />
      <CompanyGroupNetwork companies={currentResult.companies} links={currentResult.links} />
    </Stack>
  );
};

function getRole(company: CompanyGroupCompany | undefined): CompanyGroupRole {
  if (isNil(company)) {
    return 'none';
  }
  if (company.isMember) {
    return 'member';
  }
  return company.isHub ? 'hub' : 'none';
}

async function loadCompanyGroup(client: ApolloClient<object>, companyId: string): Promise<CompanyGroup> {
  const [asMember, asHub] = await Promise.all([
    fetchCompanyGroupRelationships(client, { sourceCompanyId: companyId }),
    fetchCompanyGroupRelationships(client, { targetCompanyId: companyId }),
  ]);

  const hubIds = new Set(asMember.map((relationship) => relationship.targetCompany.company_id));
  if (asHub.length > 0) {
    hubIds.add(companyId);
  }

  const relationships = (
    await Promise.all(Array.from(hubIds, (hubId) => fetchCompanyGroupRelationships(client, { targetCompanyId: hubId })))
  ).flat();

  return {
    companyId,
    companies: collectCompanies(relationships, companyId),
    links: relationships.map(({ companyRelationshipId, sourceCompany, targetCompany }) => ({
      id: companyRelationshipId,
      hubId: targetCompany.company_id,
      memberId: sourceCompany.company_id,
      label: `${sourceCompany.company_name ?? sourceCompany.company_id} is a member of ${targetCompany.company_name ?? targetCompany.company_id}`,
    })),
  };
}

function collectCompanies(relationships: CompanyGroupRelationship[], currentCompanyId: string): CompanyGroupCompany[] {
  const companies = new Map<string, CompanyGroupCompany>();

  const upsert = (company: CompanyGroupRelationship['sourceCompany'], role: 'isHub' | 'isMember') => {
    const existing = companies.get(company.company_id) ?? {
      id: company.company_id,
      name: company.company_name,
      isTest: company.is_test === true,
      isHub: false,
      isMember: false,
      isCurrent: company.company_id === currentCompanyId,
    };
    companies.set(company.company_id, { ...existing, [role]: true });
  };

  for (const { sourceCompany, targetCompany } of relationships) {
    upsert(targetCompany, 'isHub');
    upsert(sourceCompany, 'isMember');
  }

  return Array.from(companies.values());
}
